import { FaRegTrashAlt } from "react-icons/fa";
import {
  ActionsContainer,
  ButtonGoBack,
  FormContainer,
  FormSection,
  InactiveInfo,
  ReturnButton,
  SectionTitle,
  SectionTitleAction,
  RadioContainer,
  SwitchContainer,
} from "./styles";

import { useState, useEffect, useMemo, useCallback } from "react";
import { useIntl } from "react-intl";
import { useHistory, useParams, useLocation } from "react-router-dom";
import { useForm, useFieldArray } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Tooltip, Radio } from "antd";
import AnimatedMainContainer from "../../../components/AnimatedMainContainer";
import { useToast } from "../../../hooks/toast";
import uploadFlowService from "../../../service/uploadFlow.service";
import { getEcommerceProductsService } from "../../../service/ecommerce.service";
import { getSalesforceProductsService } from "../../../service/uploadFlow.service";
import { useSelector } from "react-redux";
import DeleteFlowModal from "../components/DeleteModalFlow";
import DocumentsList from "./components/DocumentsList";
import FormActions from "./components/FormActions";
import FlowBasicInfo from "./components/FlowBasicInfo";
import FlowTypeSelection from "./components/FlowTypeSelection";
import ControlledSelect from "../../../components/ControlledInputs/select";
import Switch from "../../../components/Switch";
import useProvisionTypes from "../../../hooks/useProvisionTypes";
import userTypesConstants from "../../../constants/userTypes";
import {
  actorTypes,
  boothRequirementTypes,
} from "../../../constants/actorTypes";
import useFlowOptions from "../../../hooks/useFlowOptions";
import useFlowTranslations from "../../../hooks/useFlowTranslations";
import useFlowConstants from "../../../hooks/useFlowConstants";
import {
  extractUrlParameters,
  getPageTitle,
  transformDocuments,
  SUCCESS_CODES,
} from "../../../utils/flowUtils";
import {
  getDefaultFlowValues,
  cleanFormData,
  transformFormDataToApi,
  processFlowDataForEdit,
  DELETE_SUCCESS_CODES,
} from "../../../utils/flowDataUtils";
import {
  IoCheckmarkDoneOutline,
  IoReturnUpBackOutline,
  IoTrashOutline,
} from "react-icons/io5";
import CustomModalConfirmation from "../../../components/CustomModalConfirmation";
import { MdError } from "react-icons/md";

// Códigos de erro retornados pelo backend ao salvar o fluxo
const API_ERROR_CODES = {
  ALL_AND_OTHERS_NOT_ALLOWED: -48020,
  PACKAGE_REQUIRES_MIN_FILES: -48017,
  PACKAGE_STRUCTURAL_CHANGE_NOT_ALLOWED: -48026,
};

// Mapa: código de erro do backend → chave i18n da mensagem exibida no toast
const API_ERROR_MESSAGES = {
  [API_ERROR_CODES.ALL_AND_OTHERS_NOT_ALLOWED]: "error.-48020",
  [API_ERROR_CODES.PACKAGE_REQUIRES_MIN_FILES]:
    "document_package_requires_at_least_two_files",
  [API_ERROR_CODES.PACKAGE_STRUCTURAL_CHANGE_NOT_ALLOWED]:
    "flow.package.structural_changes_not_allowed",
};

const schema = z
  .object({
    name: z
      .string()
      .min(1, { message: "error.field_required" })
      .max(100, { message: "error.max_length-100" }),
    nameTranslations: z
      .record(
        z
          .string()
          .min(1, { message: "error.field_required" })
          .max(100, { message: "error.max_length-100" })
      )
      .default({}),
    requirementScope: z.string().min(1, { message: "error.field_required" }),
    documents: z
      .array(
        z.object({
          fileName: z.string().min(1, { message: "error.field_required" }),
          fileNameTranslations: z
            .record(
              z
                .string()
                .min(1, { message: "error.field_required" })
                .max(100, { message: "error.max_length-100" })
            )
            .default({}),
          allowedFileTypes: z
            .array(
              z.object({
                value: z.string(),
                label: z.string(),
              })
            )
            .min(1, { message: "error.field_required" }),
          productsEcommerce: z
            .array(
              z.object({
                value: z.string(),
                label: z.string(),
              })
            )
            .optional(),
          mandatory: z.boolean().default(false),
          insuranceDashboard: z.boolean().default(false),
        })
      )
      .min(1, { message: "error.field_required" }),
    allowClient: z.boolean().default(true),
    allowServiceProvider: z.boolean().default(false),
    allowedClientTypes: z.any().default([]),
    allowedServiceTypes: z.any().default([]),
    allowedActorTypes: z.any().default([]),
    boothRequirement: z.any().default(null),
    contractProducts: z
      .array(
        z.object({
          value: z.string(),
          label: z.string(),
        })
      )
      .optional()
      .default([]),
    documentPackage: z.boolean().default(false),
    active: z.boolean().default(true),
  })
  .superRefine((data, ctx) => {
    if (data.allowClient) {
      const hasValidAllowedClientTypes =
        Array.isArray(data.allowedClientTypes) &&
        data.allowedClientTypes.length > 0;

      if (!hasValidAllowedClientTypes) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["allowedClientTypes"],
          message: "error.field_required",
        });
      }
    }

    if (data.allowServiceProvider) {
      const hasValidAllowedServiceTypes =
        Array.isArray(data.allowedServiceTypes) &&
        data.allowedServiceTypes.length > 0;

      if (!hasValidAllowedServiceTypes) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["allowedServiceTypes"],
          message: "error.field_required",
        });
      }
    }

    const hasValidAllowedActorTypes =
      Array.isArray(data.allowedActorTypes) &&
      data.allowedActorTypes.length > 0;

    if (!hasValidAllowedActorTypes) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["allowedActorTypes"],
        message: "error.field_required",
      });
    } else {
      const hasAll = data.allowedActorTypes.some(
        (type) => type?.value === "ALL" || type === "ALL"
      );
      const hasOthers = data.allowedActorTypes.some(
        (type) => type?.value !== "ALL" && type !== "ALL"
      );

      if (hasAll && hasOthers) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["allowedActorTypes"],
          message: "error.-48020",
        });
      }
    }

    if (!data.boothRequirement) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["boothRequirement"],
        message: "error.field_required",
      });
    }
  });

const Flow = () => {
  const { token } = useSelector((state) => state.auth);
  const intl = useIntl();
  const { provisionTypes: provisionTypesLocalized } = useProvisionTypes();
  const userTypesLocalized = useMemo(() => userTypesConstants(intl), [intl]);
  const actorTypesLocalized = useMemo(() => actorTypes(intl), [intl]);
  const boothRequirementTypesLocalized = useMemo(
    () => boothRequirementTypes(intl),
    [intl]
  );
  const history = useHistory();
  const params = useParams();
  const location = useLocation();
  const { addToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [initialData, setInitialData] = useState(null);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingFlow, setDeletingFlow] = useState(false);
  const [tempDocuments, setTempDocuments] = useState([]);
  const [ecommerceProducts, setEcommerceProducts] = useState([]);
  const { id, action } = extractUrlParameters(params, location);
  const [isActive, setIsActive] = useState();
  const [hasExecutions, setHasExecutions] = useState();
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [confirmEdit, setConfirmEdit] = useState(false);
  const [
    modalConfirmDeleteDocumentWithExecution,
    setModalConfirmDeleteDocumentWithExecution,
  ] = useState(false);
  const [pendingRemove, setPendingRemove] = useState(null);
  const [salesforceProducts, setSalesforceProducts] = useState([]);
  const [loadingSalesforceProducts, setLoadingSalesforceProducts] =
    useState(false);

  const {
    clientTypeOptions,
    serviceTypeOptions,
    setClientTypeOptions,
    setServiceTypeOptions,
    loadOptions,
    processFlowOptionsForEdit,
    mapSelectedValues,
  } = useFlowOptions(token, intl, userTypesLocalized, provisionTypesLocalized);

  const {
    availableTranslations,
    fileAvailableTranslations,
    setFileAvailableTranslations,
    initializeAvailableTranslations,
    handleAddTranslation,
    handleRemoveTranslation,
    initializeFileAvailableTranslations,
    handleAddFileTranslation,
    handleRemoveFileTranslation,
  } = useFlowTranslations(intl);

  const { MESSAGES, DELETE_MESSAGES } = useFlowConstants(intl);

  const fillDefaultFlowValues = useCallback(
    (defaultValues, actorTypesLocalized, boothRequirementTypesLocalized) => {
      const defaultAllOption = actorTypesLocalized.find(
        (at) => at.value === "ALL"
      );
      const defaultBoothOption = boothRequirementTypesLocalized.find(
        (br) => br.value === "BOTH"
      );

      if (defaultAllOption) {
        defaultValues.allowedActorTypes = [defaultAllOption];
      }
      if (defaultBoothOption) {
        defaultValues.boothRequirement = defaultBoothOption;
      }

      return defaultValues;
    },
    []
  );

  const compareArrays = (arr1, arr2) => {
    if (!arr1 || !arr2) return arr1 === arr2;
    if (arr1.length !== arr2.length) return false;

    return arr1.every((item1, index) => {
      const item2 = arr2[index];
      if (!item1 || !item2) return item1 === item2;

      if (item1.value !== undefined && item1.label !== undefined) {
        return item1.value === item2.value && item1.label === item2.label;
      }

      return JSON.stringify(item1) === JSON.stringify(item2);
    });
  };

  const captureTempDocuments = () => {
    const currentData = form.getValues();

    if (currentData.documents) {
      const newDocs = currentData.documents.filter(
        (doc) =>
          !doc.documentConfigId &&
          (doc.fileName?.trim() ||
            doc.allowedFileTypes?.length > 0 ||
            doc.mandatory !== false ||
            doc.productsEcommerce?.length > 0 ||
            Object.keys(doc.fileNameTranslations || {}).length > 0)
      );

      const modifiedExistingDocs = currentData.documents
        .filter((doc) => doc.documentConfigId)
        .map((currentDoc) => {
          const originalDoc = initialData?.documents?.find(
            (original) =>
              original.documentConfigId === currentDoc.documentConfigId
          );

          if (originalDoc) {
            const hasChanges =
              currentDoc.fileName !== originalDoc.fileName ||
              JSON.stringify(currentDoc.fileNameTranslations) !==
                JSON.stringify(originalDoc.fileNameTranslations) ||
              !compareArrays(
                currentDoc.allowedFileTypes,
                originalDoc.allowedFileTypes
              ) ||
              currentDoc.mandatory !== originalDoc.mandatory ||
              !compareArrays(
                currentDoc.productsEcommerce,
                originalDoc.productsEcommerce
              );

            return hasChanges ? currentDoc : null;
          }
          return null;
        })
        .filter(Boolean);

      const allTempDocs = [...newDocs, ...modifiedExistingDocs];
      setTempDocuments(allTempDocs);
      return allTempDocs;
    }
    return [];
  };

  const combineDocuments = (apiDocs, tempDocs = tempDocuments) => {
    const transformedApiDocs = transformDocuments(apiDocs);

    const tempDocsMap = new Map();
    tempDocs.forEach((tempDoc) => {
      if (tempDoc.documentConfigId) {
        tempDocsMap.set(tempDoc.documentConfigId, tempDoc);
      }
    });

    const updatedApiDocs = transformedApiDocs.map((apiDoc) => {
      const tempVersion = tempDocsMap.get(apiDoc.documentConfigId);
      return tempVersion || apiDoc;
    });

    const newTempDocs = tempDocs.filter((doc) => !doc.documentConfigId);

    return [...updatedApiDocs, ...newTempDocs];
  };

  const loadEcommerceProducts = useCallback(async () => {
    try {
      const response = await getEcommerceProductsService(token);
      const products = response.data?.ecommerceChlebaProductFilterOutput || [];

      const formattedProducts = products.map((product) => ({
        value: product.code,
        label: intl.locale === "pt" ? product.title : product.englishTitle,
      }));

      setEcommerceProducts(formattedProducts);
    } catch (error) {
      setEcommerceProducts([]);
    }
  }, [token, intl.locale]);

  const loadSalesforceProducts = useCallback(async () => {
    setLoadingSalesforceProducts(true);
    try {
      const response = await getSalesforceProductsService(token);
      const products = response.data?.output || [];

      const formattedProducts = products.map((product) => ({
        value: product.productCode,
        label: `${product.productCode} - ${product.name}`,
        productCode: product.productCode,
        name: product.name,
      }));

      setSalesforceProducts(formattedProducts);
    } catch (error) {
      console.error("Erro ao carregar produtos do Salesforce:", error);
      setSalesforceProducts([]);
      addToast({
        type: "error",
        title: intl.formatMessage({ id: "error" }),
        description: intl.formatMessage({ id: "contract_products_error" }),
      });
    } finally {
      setLoadingSalesforceProducts(false);
    }
  }, [token, intl, addToast]);

  useEffect(() => {
    const loadData = async () => {
      try {
        await loadEcommerceProducts();
        await loadSalesforceProducts();

        const { localizedUserTypes, localizedServices } = await loadOptions();

        if (id && action === "edit") {
          setLoading(true);
          try {
            const response = await uploadFlowService.getFlow(id, token);
            const flow = response.data.flows[0];
            setIsActive(flow.active && action !== "edit" ? false : flow.active);
            setHasExecutions(flow.hasExecutions);

            const { finalUserTypes, finalServices } = processFlowOptionsForEdit(
              flow,
              localizedUserTypes,
              localizedServices
            );
            setConfirmEdit(flow.hasExecutions || false);
            setClientTypeOptions(finalUserTypes);
            setServiceTypeOptions(finalServices);

            const { mappedAllowedClientTypes, mappedAllowedServiceTypes } =
              mapSelectedValues(flow, finalUserTypes, finalServices);

            const transformedData = processFlowDataForEdit(
              flow,
              transformDocuments,
              mappedAllowedClientTypes,
              mappedAllowedServiceTypes,
              actorTypesLocalized,
              boothRequirementTypesLocalized
            );

            setInitialData(transformedData);

            initializeAvailableTranslations(flow.nameTranslations || {});

            transformedData.documents.forEach((doc, index) => {
              initializeFileAvailableTranslations(
                index,
                doc.fileNameTranslations || {}
              );
            });
          } catch (err) {
            setInitialData(getDefaultFlowValues());
          } finally {
            setLoading(false);
            setDataLoaded(true);
          }
        } else {
          setIsActive(true);
          setHasExecutions(false);

          const defaultValues = fillDefaultFlowValues(
            getDefaultFlowValues(),
            actorTypesLocalized,
            boothRequirementTypesLocalized
          );

          setInitialData(defaultValues);
          initializeAvailableTranslations({});
          initializeFileAvailableTranslations(0, {});
          setDataLoaded(true);
        }
      } catch (err) {
        const defaultValues = fillDefaultFlowValues(
          getDefaultFlowValues(),
          actorTypesLocalized,
          boothRequirementTypesLocalized
        );

        setInitialData(defaultValues);
        initializeAvailableTranslations({});
        initializeFileAvailableTranslations(0, {});
        setDataLoaded(true);
      }
    };

    loadData();
  }, [
    id,
    action,
    token,
    loadOptions,
    processFlowOptionsForEdit,
    mapSelectedValues,
    setClientTypeOptions,
    setServiceTypeOptions,
    initializeAvailableTranslations,
    initializeFileAvailableTranslations,
    loadEcommerceProducts,
    loadSalesforceProducts,
    actorTypesLocalized,
    boothRequirementTypesLocalized,
    fillDefaultFlowValues,
  ]);

  const form = useForm({
    resolver: zodResolver(schema),
    mode: "onTouched",
    defaultValues: getDefaultFlowValues(),
  });

  const { control, handleSubmit, formState, watch, reset, setValue } = form;
  const { fields, append, remove } = useFieldArray({
    control,
    name: "documents",
  });

  useEffect(() => {
    if (dataLoaded && initialData) {
      reset(initialData);
    }
  }, [dataLoaded, initialData, reset]);

  useEffect(() => {
    if (dataLoaded && ecommerceProducts.length > 0) {
      const currentValues = form.getValues();
      if (currentValues.documents?.length > 0) {
        const hasProductsToUpdate = currentValues.documents.some(
          (doc) => doc.productsEcommerce?.length > 0
        );

        if (hasProductsToUpdate) {
          const updatedDocuments = currentValues.documents.map((doc) => {
            if (doc.productsEcommerce?.length > 0) {
              const updatedProducts = doc.productsEcommerce.map(
                (selectedProduct) => {
                  const matchingProduct = ecommerceProducts.find(
                    (product) => product.value === selectedProduct.value
                  );
                  return matchingProduct || selectedProduct;
                }
              );
              return { ...doc, productsEcommerce: updatedProducts };
            }
            return doc;
          });
          setValue("documents", updatedDocuments, { shouldDirty: false });
        }
      }
    }
  }, [ecommerceProducts, dataLoaded, form, setValue]);

  useEffect(() => {
    const subscription = watch((value, { name }) => {
      if (name === "allowClient") {
        setTimeout(() => form.trigger("allowedClientTypes"), 100);
      }
      if (name === "allowServiceProvider") {
        setTimeout(() => form.trigger("allowedServiceTypes"), 100);
      }
    });
    return () => subscription.unsubscribe();
  }, [watch, form]);

  const watchedFiles = watch("documents");

  const handleDocumentPackageChange = useCallback(
    (checked) => {
      setValue("documentPackage", checked, { shouldValidate: true });

      // Ao ativar o pacote, marcar todos os documentos existentes como obrigatórios
      if (checked) {
        const currentDocuments = form.getValues("documents") || [];
        const updatedDocuments = currentDocuments.map((doc) => ({
          ...doc,
          mandatory: true,
        }));
        setValue("documents", updatedDocuments, {
          shouldDirty: true,
          shouldValidate: true,
        });
      }
    },
    [setValue, form]
  );

  const showSuccessToast = (message) => {
    addToast({
      type: "success",
      title: intl.formatMessage({ id: "success" }),
      description: message,
    });

    setTempDocuments([]);
    history.push("/admin/upload-documents");
  };

  const showErrorToast = useCallback(
    (message) => {
      addToast({
        type: "error",
        title: intl.formatMessage({ id: "error" }),
        description: message,
      });
    },
    [addToast, intl]
  );

  const handleApiResponse = (response, isEdit) => {
    const code =
      response.data?.code || response.data?.data?.code || response.status;
    const successCodes = isEdit ? SUCCESS_CODES.EDIT : SUCCESS_CODES.CREATE;
    const successMessage = isEdit
      ? MESSAGES.SUCCESS.EDIT
      : MESSAGES.SUCCESS.CREATE;
    const errorMessage = isEdit ? MESSAGES.ERROR.EDIT : MESSAGES.ERROR.CREATE;

    if (successCodes.includes(code)) {
      showSuccessToast(successMessage);
    } else {
      showErrorToast(errorMessage);
    }
  };

  const reloadData = async (withTempDocs = false, tempDocs = []) => {
    if (id && action === "edit") {
      setLoading(true);
      try {
        const response = await uploadFlowService.getFlow(id, token);
        const flow = response.data.flows[0];
        setIsActive(flow.active && action !== "edit" ? false : flow.active);
        setHasExecutions(flow.hasExecutions);
        const apiDocuments = flow.documents;

        const finalDocuments = withTempDocs
          ? combineDocuments(apiDocuments, tempDocs)
          : transformDocuments(apiDocuments);

        const localizedServiceTypes = Array.isArray(flow.allowedServiceTypes)
          ? flow.allowedServiceTypes.map((serviceType) => {
              const valueAsNumber = Number(serviceType);
              const match = provisionTypesLocalized.find(
                (pt) => pt.value === valueAsNumber
              );
              return match
                ? { value: serviceType, label: match.label }
                : { value: serviceType, label: serviceType };
            })
          : [];

        const localizedUserTypesEdit = Array.isArray(flow.allowedClientTypes)
          ? flow.allowedClientTypes.map((clientType) => {
              const value = clientType?.value || clientType;
              const match = userTypesLocalized.find(
                (u) => u.optionName === value
              );
              return match
                ? { value, label: match.label }
                : { value, label: value };
            })
          : [];

        const mappedAllowedActorTypes = Array.isArray(flow.allowedActorTypes)
          ? flow.allowedActorTypes.map((actorType) => {
              const value =
                typeof actorType === "string" ? actorType : actorType.value;
              const match = actorTypesLocalized?.find(
                (at) => at.value === value
              );
              return match || { value, label: value };
            })
          : [
              {
                value: "ALL",
                label: intl.formatMessage({ id: "upload_flow_actor_type_all" }),
              },
            ];

        const mappedBoothRequirement = flow.boothRequirement
          ? boothRequirementTypesLocalized?.find(
              (br) => br.value === flow.boothRequirement
            ) || { value: flow.boothRequirement, label: flow.boothRequirement }
          : {
              value: "BOTH",
              label: intl.formatMessage({
                id: "upload_flow_booth_requirement_both",
              }),
            };

        const transformedData = {
          ...flow,
          documents: finalDocuments,
          allowClient: !!flow.allowClient,
          allowServiceProvider: !!flow.allowServiceProvider,
          allowedClientTypes: localizedUserTypesEdit,
          allowedServiceTypes: localizedServiceTypes,
          allowedActorTypes: mappedAllowedActorTypes,
          boothRequirement: mappedBoothRequirement,
          nameTranslations: flow.nameTranslations || {},
          documentPackage: !!flow.documentPackage,
        };
        setInitialData(transformedData);

        initializeAvailableTranslations(flow.nameTranslations || {});

        transformedData.documents.forEach((doc, index) => {
          initializeFileAvailableTranslations(
            index,
            doc.fileNameTranslations || {}
          );
        });
      } catch (err) {
        setInitialData(getDefaultFlowValues());
      } finally {
        setLoading(false);
        setDataLoaded(true);
      }
    } else {
      setIsActive(true);
      setHasExecutions(false);
      const defaultValues = fillDefaultFlowValues(
        getDefaultFlowValues(),
        actorTypesLocalized,
        boothRequirementTypesLocalized
      );

      setInitialData(defaultValues);
      initializeAvailableTranslations({});
      initializeFileAvailableTranslations(0, {});
      setDataLoaded(true);
    }
  };

  const reloadPartialData = async (withTempDocs = false, tempDocs = []) => {
    if (id && action === "edit") {
      setLoading(true);
      try {
        const response = await uploadFlowService.getFlow(id, token);
        const flow = response.data.flows[0];
        const apiDocuments = flow.documents;

        const finalDocuments = withTempDocs
          ? combineDocuments(apiDocuments, tempDocs)
          : transformDocuments(apiDocuments);

        setValue("documents", finalDocuments);

        if (initialData) {
          const currentFormData = form.getValues();
          setInitialData({
            ...currentFormData,
            documents: finalDocuments,
          });
        }

        finalDocuments.forEach((doc, index) => {
          initializeFileAvailableTranslations(
            index,
            doc.fileNameTranslations || {}
          );
        });
      } catch (err) {
        console.error("Erro ao recarregar documentos:", err);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleRemoveDocumentFromFlow = (
    index,
    documentConfigId,
    hasExecutions
  ) => {
    const capturedTempDocs = captureTempDocuments();

    uploadFlowService
      .removeDocumentFromFlow(documentConfigId, token)
      .then((response) => {
        if (
          [200, 201].includes(response.status) ||
          [200, 201].includes(response.data?.code)
        ) {
          addToast({
            type: "success",
            title: intl.formatMessage({ id: "success" }),
            description: intl.formatMessage({
              id: "flow.document.deleted.success",
            }),
          });

          reloadPartialData(true, capturedTempDocs);
          setTempDocuments([]);
        } else {
          throw new Error(
            `${intl.formatMessage({ id: "unexpected_response_code" })} ${
              response.data?.code
            }`
          );
        }
      })
      .catch((error) => {
        const errorCode = error?.response?.data?.code;
        let errorMessage;

        switch (errorCode) {
          case -47001:
            errorMessage = intl.formatMessage({
              id: "flow.document.deleted.error.with_executions",
            });
            break;
          case -47002:
            errorMessage = intl.formatMessage({
              id: "flow.document.deleted.error.last_document",
            });
            break;
          default:
            errorMessage = intl.formatMessage({
              id: "flow.document.deleted.error",
            });
            break;
        }

        addToast({
          type: "error",
          title: intl.formatMessage({ id: "error" }),
          description: errorMessage,
        });
      });
  };

  const updateFlow = async (uploadFlowRequest) => {
    try {
      const response = await uploadFlowService.updateFlow(
        id,
        uploadFlowRequest,
        token
      );
      handleApiResponse(response, true);
    } catch (error) {
      const errorCode = error?.response?.data?.code;
      const messageKey = API_ERROR_MESSAGES[errorCode];
      const errorMessage = messageKey
        ? intl.formatMessage({ id: messageKey })
        : MESSAGES.ERROR.EDIT;

      showErrorToast(errorMessage);
    }
  };

  const createFlow = async (uploadFlowRequest) => {
    try {
      const response = await uploadFlowService.createFlow(
        uploadFlowRequest,
        token
      );
      handleApiResponse(response, false);
    } catch (error) {
      const errorCode = error?.response?.data?.code;
      const messageKey = API_ERROR_MESSAGES[errorCode];
      const errorMessage = messageKey
        ? intl.formatMessage({ id: messageKey })
        : MESSAGES.ERROR.CREATE;

      showErrorToast(errorMessage);
    }
  };

  const onConfirmationSubmit = async () => {
    if (confirmEdit) setModalIsOpen(true);
  };

  const onSubmit = async (data) => {
    if (!data.allowClient && !data.allowServiceProvider) {
      showErrorToast(
        intl.formatMessage({ id: "flow.at_least_one_type_required" })
      );
      return;
    }

    const convertTranslationKeys = (translations) => {
      if (!translations || Object.keys(translations).length === 0) {
        return {};
      }

      const currentLocale = intl.locale;
      const isPortuguese = currentLocale === "pt" || currentLocale === "pt-BR";
      const alternativeKey = isPortuguese ? "en_us" : "pt_br";

      const converted = {};
      Object.entries(translations).forEach(([, value]) => {
        if (value && value.trim() !== "") {
          converted[alternativeKey] = value;
        }
      });

      return converted;
    };

    const cleanedData = cleanFormData(data);

    const dataWithCorrectKeys = {
      ...cleanedData,
      nameTranslations: convertTranslationKeys(cleanedData.nameTranslations),
      documents: cleanedData.documents.map((doc) => ({
        ...doc,
        fileNameTranslations: convertTranslationKeys(doc.fileNameTranslations),
      })),
    };

    const uploadFlowRequest = transformFormDataToApi(
      dataWithCorrectKeys,
      id,
      action,
      initialData
    );

    setLoading(true);

    try {
      const isEditMode = id && action === "edit";
      if (isEditMode) {
        await updateFlow(uploadFlowRequest);
      } else {
        await createFlow(uploadFlowRequest);
      }
    } catch (error) {
      showErrorToast(intl.formatMessage({ id: "flow.unexpected.error" }));
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setTempDocuments([]);
    reloadData();
    history.push("/admin/upload-documents");
  };

  const handleAddFile = () => {
    const newIndex = fields.length;
    const isDocumentPackage = form.getValues("documentPackage");
    append({
      fileName: "",
      fileNameTranslations: {},
      allowedFileTypes: [],
      mandatory: !!isDocumentPackage,
      insuranceDashboard: false,
    });

    setTimeout(() => {
      initializeFileAvailableTranslations(newIndex, {});
    }, 0);
  };

  const handleRemoveFile = async (index, fileId, hasExecutions) => {
    if (hasExecutions) {
      setPendingRemove({ index, fileId, hasExecutions });
      setModalConfirmDeleteDocumentWithExecution(true);
      return;
    }

    await executeRemoveFile(index, fileId, hasExecutions);
  };

  const executeRemoveFile = async (index, fileId, hasExecutions) => {
    if (action === "edit" && fileId) {
      await handleRemoveDocumentFromFlow(index, fileId, hasExecutions);
      return;
    }

    if (fields.length <= 1) return;

    setFileAvailableTranslations((prev) => {
      const updated = { ...prev };
      delete updated[index];

      const newUpdated = {};
      Object.keys(updated).forEach((key) => {
        const keyIndex = Number(key);
        newUpdated[keyIndex > index ? keyIndex - 1 : keyIndex] = updated[key];
      });

      return newUpdated;
    });

    remove(index);

    setTimeout(() => form.trigger(), 0);
  };

  const handleConfirmRemoveFileWithExecution = async () => {
    if (!pendingRemove) return;

    const { index, fileId, hasExecutions } = pendingRemove;

    await executeRemoveFile(index, fileId, hasExecutions);

    setPendingRemove(null);
    setModalConfirmDeleteDocumentWithExecution(false);
  };

  const handleCancelRemoveFileWithExecution = () => {
    setPendingRemove(null);
    setModalConfirmDeleteDocumentWithExecution(false);
  };

  const handleRemoveFlow = async (flowId) => {
    if (!flowId) return;

    setDeletingFlow(true);

    try {
      const response = await uploadFlowService.deleteFlow(flowId, token);

      if (DELETE_SUCCESS_CODES.includes(response.data.code)) {
        addToast({
          type: "success",
          title: intl.formatMessage({ id: "success" }),
          description: DELETE_MESSAGES.SUCCESS,
        });

        history.push("/admin/upload-documents");
      } else {
        throw new Error(
          `${intl.formatMessage({ id: "unexpected_response_code" })} ${
            response.data.code
          }`
        );
      }
    } catch (error) {
      const { code } = error.response
        ? typeof error.response === "string"
          ? JSON.parse(error.response.data)
          : error.response.data
        : {};

      switch (code) {
        case -48006:
          addToast({
            type: "error",
            title: intl.formatMessage({ id: "error" }),
            description: intl.formatMessage({
              id: "flow.document.deleted.error.with_executions",
            }),
          });
          break;
        default:
          addToast({
            type: "error",
            title: intl.formatMessage({ id: "error" }),
            description: DELETE_MESSAGES.ERROR,
          });
          break;
      }
    } finally {
      setDeletingFlow(false);
      setShowDeleteModal(false);
    }
  };

  const handleShowDeleteModal = () => setShowDeleteModal(true);
  const handleCloseDeleteModal = () => setShowDeleteModal(false);
  const handleConfirmDelete = () => {
    if (id) handleRemoveFlow(id);
  };

  const salesforceOptions = useMemo(
    () =>
      salesforceProducts.map((p) => ({
        value: p.productCode,
        label: `${p.productCode} - ${p.name}`,
        productCode: p.productCode,
        name: p.name,
      })),
    [salesforceProducts]
  );

  return (
    <AnimatedMainContainer breadcrumb={getPageTitle(action, intl)}>
      <FormContainer>
        <form onSubmit={handleSubmit(onSubmit)}>
          <FormSection>
            <SectionTitleAction>
              <SectionTitle>
                {intl.formatMessage({ id: "ABOUT_FLOW" })}
              </SectionTitle>

              {action === "edit" && isActive ? (
                <button data-test="delete-flow-button" type="button" onClick={handleShowDeleteModal}>
                  <Tooltip
                    title={intl.formatMessage({
                      id: "admin.documents.delete_flow",
                    })}
                  >
                    <FaRegTrashAlt size={18} />
                  </Tooltip>
                </button>
              ) : action === "edit" && !isActive && isActive !== undefined ? (
                <InactiveInfo>
                  {intl.formatMessage({ id: "flow.inactive" })}
                </InactiveInfo>
              ) : (
                <></>
              )}
            </SectionTitleAction>
            <FlowBasicInfo
              control={control}
              formState={formState}
              watch={watch}
              availableTranslations={availableTranslations}
              handleAddTranslation={handleAddTranslation}
              handleRemoveTranslation={handleRemoveTranslation}
              form={form}
              inactivate={!isActive}
            />
          </FormSection>

          <FormSection>
            <SwitchContainer>
              <Switch
                data-test="document-package-switch"
                disabled={!isActive}
                key="document-package"
                checked={watch("documentPackage") || false}
                onChange={handleDocumentPackageChange}
              />
              <span>{intl.formatMessage({ id: "document_package" })}</span>
            </SwitchContainer>

            <SectionTitle>
              {intl.formatMessage({ id: "type" })}
              <span style={{ color: "#dc2626", marginLeft: "3px" }}>*</span>
            </SectionTitle>

            <RadioContainer>
              <Radio.Group
                value={watch("requirementScope")}
                onChange={(e) =>
                  setValue("requirementScope", e.target.value, {
                    shouldValidate: true,
                    shouldDirty: true,
                  })
                }
                disabled={!isActive}
                data-test="new-upload-flow-requirement-scope"
              >
                <Radio value="BY_CONTRACT">
                  {intl.formatMessage({ id: "flow.type.by_contract" })}
                </Radio>
                <Radio value="BY_PROFILE">
                  {intl.formatMessage({ id: "flow.type.by_profile" })}
                </Radio>
              </Radio.Group>
            </RadioContainer>
            {formState.errors.requirementScope && (
              <span
                style={{
                  color: "#dc2626",
                  fontSize: "10px",
                  marginTop: "8px",
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                  fontWeight: "600",
                  lineHeight: "18.86px",
                  fontFamily: "Open Sans, sans-serif",
                }}
              >
                <MdError color="#DC2626" size={14} />
                {intl.formatMessage({
                  id: formState.errors.requirementScope.message,
                })}
              </span>
            )}
          </FormSection>

          <FormSection>
            <SectionTitle>
              {intl.formatMessage({ id: "flow_available_for" })}
              {(watch("allowClient") || watch("allowServiceProvider")) && (
                <span style={{ color: "#dc2626", marginLeft: "3px" }}>*</span>
              )}
            </SectionTitle>

            <FlowTypeSelection
              control={control}
              formState={formState}
              watch={watch}
              form={form}
              clientTypeOptions={clientTypeOptions}
              serviceTypeOptions={serviceTypeOptions}
              actorTypeOptions={actorTypesLocalized}
              boothRequirementOptions={boothRequirementTypesLocalized}
              inactivate={!isActive}
            />
          </FormSection>

          <FormSection>
            <SectionTitle>
              {intl.formatMessage({ id: "DOCUMENTS_FLOW" })}
            </SectionTitle>
            <DocumentsList
              inactivate={!isActive}
              fields={fields}
              control={control}
              formState={formState}
              watch={watch}
              watchedFiles={watchedFiles}
              setValue={setValue}
              form={form}
              fileAvailableTranslations={fileAvailableTranslations}
              handleAddFileTranslation={handleAddFileTranslation}
              handleRemoveFileTranslation={handleRemoveFileTranslation}
              handleAddFile={handleAddFile}
              handleRemoveFile={handleRemoveFile}
              ecommerceProducts={ecommerceProducts}
              isDocumentPackage={watch("documentPackage") || false}
            />
          </FormSection>

          <FormSection>
            <SectionTitle>
              {intl.formatMessage({ id: "contract_products_salesforce" })}
            </SectionTitle>

            <ControlledSelect
              data-test="contract-products-select"
              name="contractProducts"
              control={control}
              formState={formState}
              label="contract_products_salesforce_label"
              placeholder={
                loadingSalesforceProducts
                  ? intl.formatMessage({ id: "contract_products_loading" })
                  : intl.formatMessage({
                      id: "contract_products_salesforce_placeholder",
                    })
              }
              options={salesforceOptions}
              required={false}
              isMulti={true}
              closeMenuOnSelect={false}
              disabled={!isActive || loadingSalesforceProducts}
              description="contract_products_salesforce_description"
              errors={{ contractProducts: formState.errors.contractProducts }}
              width="100%"
            />
          </FormSection>

          {isActive ? (
            <FormActions
              loading={loading}
              onCancel={handleCancel}
              onSubmit={handleSubmit(
                confirmEdit ? onConfirmationSubmit : onSubmit
              )}
            />
          ) : (
            <ActionsContainer>
              <ButtonGoBack>
                <ReturnButton data-test="go-back-button" type="button" onClick={handleCancel}>
                  <IoReturnUpBackOutline size={24} />
                  {intl.formatMessage({ id: "goback" })}
                </ReturnButton>
              </ButtonGoBack>
            </ActionsContainer>
          )}
        </form>
      </FormContainer>

      <DeleteFlowModal
        show={showDeleteModal}
        intl={intl}
        initialData={initialData}
        deletingFlow={deletingFlow}
        handleClose={handleCloseDeleteModal}
        handleConfirm={handleConfirmDelete}
        availableForInactivation={hasExecutions}
      />

      <CustomModalConfirmation
        show={modalIsOpen}
        intl={intl}
        title={intl.formatMessage({ id: "custom.modal.confirmation.title" })}
        description={intl.formatMessage({
          id: "custom.modal.confirmation.description",
        })}
        questionConfirm={intl.formatMessage({
          id: "custom.modal.confirmation.question.confirm",
        })}
        handleClose={() => setModalIsOpen(false)}
        handleConfirm={handleSubmit(onSubmit)}
        confirmText={"confirm"}
        cancelText={"goback"}
        IconConfirm={() => <IoCheckmarkDoneOutline size={20} />}
        IconCancel={() => <IoReturnUpBackOutline size={24} />}
        isLoading={loading}
      />

      <CustomModalConfirmation
        show={modalConfirmDeleteDocumentWithExecution}
        intl={intl}
        title={intl.formatMessage({ id: "confirm_delete_file_with_execution" })}
        handleClose={handleCancelRemoveFileWithExecution}
        handleConfirm={handleConfirmRemoveFileWithExecution}
        confirmText={"delete"}
        cancelText={"goback"}
        IconConfirm={() => <IoTrashOutline size={20} />}
        IconCancel={() => <IoReturnUpBackOutline size={24} />}
        isLoading={loading}
      />
    </AnimatedMainContainer>
  );
};

export default Flow;
